import type { TemplatePage } from '../templates';

const filled = [
  { item: 'Engineered oak, 3/8 in wear layer, natural matte: 640 sq ft (575 sq ft of floor plus allowance for cuts)', amount: '$4,480' },
  { item: 'Glue-down installation, family room and two bedrooms', amount: '$2,590' },
  { item: 'Remove carpet, pad and tack strip; scrape and dispose', amount: '$520' },
  { item: 'Self-leveling compound over one low area, about 40 sq ft', amount: '$260' },
  { item: 'T-molding at two doorways and a reducer at the tile hall', amount: '$145' },
];

const faq = [
  {
    q: 'Is this flooring estimate template free to use?',
    a: 'Yes. Read it on this page, download the PDF, or copy the wording into your own documents. There is no form to fill in and no email address required.',
  },
  {
    q: 'How should I show the waste allowance?',
    a: 'As part of the material quantity, with the floor area next to it, like the example: the square footage you will install and the larger amount you will order. The right allowance depends on the product, the pattern and the room shapes, so the template leaves the number to you.',
  },
  {
    q: 'Do I need to include a deposit on a flooring estimate?',
    a: 'That is your decision. If you take one, the template has a line for the amount, when it is due and what happens to it if the job is cancelled after material is ordered.',
  },
  {
    q: 'Can my customer sign this estimate online instead of on paper?',
    a: 'If you build the same estimate in Scopie, it becomes a link with your business name on it. The customer opens it, chooses any optional lines such as new trim, and signs on their phone.',
  },
];

const page: TemplatePage = {
  slug: 'flooring-estimate-template',
  path: '/templates/flooring-estimate-template',
  primaryKeyword: 'flooring estimate template',
  secondaryKeywords: ['flooring quote template', 'flooring installation estimate template', 'free flooring estimate form'],
  title: 'Free Flooring Estimate Template (PDF + Example) | Scopie', // 55
  metaDescription:
    'A free flooring estimate template with every line installers need: material and waste, installation, tear-out, subfloor prep, transitions and terms.', // 148
  h1: 'Flooring estimate template',
  answerFirst:
    'A flooring estimate should name the product and the quantity you will order, price installation by area, and list tear-out, subfloor preparation and transitions as their own lines, followed by the terms that protect you when the old floor comes up. The free template below covers all of it, with a filled-in example.',
  lastReviewed: '2026-10-06',
  schemaType: 'DigitalDocument',
  leadSheet: true,
  blocks: [
    {
      type: 'document',
      id: 'template',
      h2: 'The template',
      intro: 'Replace anything in [brackets]. Delete lines that do not apply to the job.',
      pdfName: 'flooring-estimate-template',
      sections: [
        { heading: 'From', lines: ['[Business name]', '[Phone] · [Email]', '[License number, if your state requires one]'] },
        { heading: 'Prepared for', lines: ['[Customer name]', '[Job address]', 'Estimate [number] · Date [date] · Valid until [date]'] },
        {
          heading: 'Work and materials',
          lines: [
            'Material: [product, color, size], [sq ft to order] ([sq ft of floor] plus allowance for cuts) · [price]',
            'Who supplies material: [us / customer]',
            'Installation: [method: floating / glue-down / nail-down], [rooms] · [price]',
            'Remove and dispose of existing [carpet / tile / laminate / wood] · [price]',
            'Subfloor preparation: [leveling / moisture barrier / plywood repair] · [price]',
            'Trim and transitions: [baseboard / quarter round / thresholds / stair nosing] · [price]',
            'Move furniture and appliances: [included / customer / price]',
            'Subtotal [amount] · Tax [amount] · Total [amount]',
          ],
        },
        {
          heading: 'Schedule',
          lines: ['Material lead time: about [number] days after signing', 'Acclimation on site: [number] days before installation, if the product requires it', 'Installation: about [number] days'],
        },
        {
          heading: 'Terms',
          lines: [
            'Deposit: [amount] due when this estimate is accepted; balance due on completion. [Refund rules if cancelled after material is ordered.]',
            'Subfloor: damage, moisture or soft spots found after removal will be shown to you and priced before any repair.',
            'Not included: [for example wall repairs, painting of new trim, plumbing or appliance hook-ups].',
            'Changes to rooms, product or layout after acceptance are priced separately.',
            'Cancellation: [your terms].',
          ],
        },
        { heading: 'Acceptance', lines: ['Customer name [name]', 'Signature [signature]', 'Date [date]'] },
      ],
    },
    {
      type: 'sample-quote',
      id: 'filled',
      h2: 'The same template, filled in',
      label: 'Example: engineered oak in a family room and two bedrooms',
      lines: filled,
      total: '$7,995',
      note: 'Illustrative figures for a single job, not price guidance.',
    },
    {
      type: 'list',
      id: 'line-by-line',
      h2: 'Filling it in, line by line',
      style: 'steps',
      items: [
        { title: 'Material', text: 'Write the product exactly as you will order it, so a substitution later is obvious. Put the floor area and the order quantity side by side so the customer understands the difference.' },
        { title: 'Installation', text: 'Name the method. Glue-down, floating and nail-down take different time and tools, and customers sometimes compare a floating price with a glue-down one.' },
        { title: 'Tear-out', text: 'Removing tile set in mortar is a different day from pulling up carpet. Say what is coming up and where it goes.' },
        { title: 'Subfloor', text: 'Price the preparation you can see now, and use the subfloor clause for what you cannot see until the old floor is gone.' },
        { title: 'Transitions', text: 'Doorways, stairs and the edge where new floor meets old are where callbacks start. List each one.' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Common flooring estimate mistakes',
      style: 'mistakes',
      items: [
        { title: 'One installed price per square foot', text: 'It hides removal and prep, and the customer cannot tell what changed when the bill grows.' },
        { title: 'No validity date', text: 'Material prices and availability move; an open-ended estimate becomes a promise you cannot keep.' },
        { title: 'Lead time missing', text: 'Customers plan around the install date. Tell them how long the product takes.' },
        { title: 'Silence on furniture and appliances', text: 'Someone has to move the piano and disconnect the dishwasher. Decide who.' },
      ],
    },
    {
      type: 'text',
      id: 'online',
      h2: 'Or send it as an estimate the customer signs online',
      paragraphs: [
        'Paper and PDFs work, but they wait in an inbox. In Scopie you build the same estimate from your saved products and labor items, mark extras like new quarter round as optional, and share a link with your business name. The customer ticks the extras they want, reads your terms and signs on their phone. The signed estimate becomes a job on your schedule, and later an invoice with its own link.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'About this template', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/flooring', anchor: 'Scopie for flooring contractors', status: 'live' },
    { href: '/templates/cancellation-policy', anchor: 'cancellation policy template', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
  ],
  cta: {
    heading: 'Turn this template into a signed estimate',
    text: 'Save your flooring products and labor items in Scopie once, then send each estimate as a link after the measure.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
